import { Fragment, useEffect, useState } from 'react';

import styled from '@emotion/styled';
import toast from 'react-hot-toast';

import { PrimaryWhiteButton } from '../../../components/Button/Buttons';
import { Card, CardHead, CardTitle, Hint, Input, Scroll, SimpleTable } from '../../../components/Layout/PagePieces';
import useProviderChecks from '../../../hooks/dashboard/profit/useProviderChecks';
import { ProviderCheckRun } from '../../../models/profit/profit.model';
import { formatNaira } from '../../../utils/utils';

const money = (value: string | null) => (value == null ? '—' : formatNaira(Number(value)));
const pct = (value: string | null) => (value == null ? '—' : `${value}%`);
const PROVIDERS: Record<string, string> = { meta: 'Meta (WhatsApp)', anthropic: 'Anthropic' };
const CATEGORY: Record<string, string> = {
  utility: 'Utility',
  marketing: 'Marketing',
  authentication: 'Authentication',
  service: 'Service',
};

const yesterday = () => {
  // Lagos date, so "yesterday" is the day that has ended there.
  const lagos = new Date(new Date().toLocaleString('en-US', { timeZone: 'Africa/Lagos' }));
  lagos.setDate(lagos.getDate() - 1);
  return `${lagos.getFullYear()}-${String(lagos.getMonth() + 1).padStart(2, '0')}-${String(lagos.getDate()).padStart(2, '0')}`;
};

const describe = (name: string, result: ProviderCheckRun['meta']) => {
  if (result.status === 'checked') return `${PROVIDERS[name]}: checked`;
  if (result.status === 'skipped') return `${PROVIDERS[name]}: not set up, skipped`;
  return `${PROVIDERS[name]}: ${result.reason ?? 'failed'}`;
};

// Meta's and Anthropic's own cost for a whole day against our events (limits
// spec D32). Runs daily for the last two days; "Check a day" re-runs one.
const ProviderChecksCard = () => {
  const { checks, running, fetchChecks, checkDay } = useProviderChecks();
  const [day, setDay] = useState(yesterday());
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    fetchChecks();
    // eslint-disable-next-line
  }, []);

  return (
    <Card>
      <CardHead>
        <CardTitle>Provider checks</CardTitle>
        <Controls
          onSubmit={(e) => {
            e.preventDefault();
            checkDay(day, (run) => {
              const lines = [describe('meta', run.meta), describe('anthropic', run.anthropic)];
              const failed = run.meta.status === 'error' || run.anthropic.status === 'error';
              (failed ? toast.error : toast.success)(lines.join(' · '));
            });
          }}
        >
          <Input type='date' aria-label='Day to check' value={day} max={yesterday()} onChange={(e) => setDay(e.target.value)} />
          <PrimaryWhiteButton type='submit' disabled={running || !day}>
            {running ? 'Checking…' : 'Check a day'}
          </PrimaryWhiteButton>
        </Controls>
      </CardHead>
      <Hint>
        Each provider’s own cost for a full day against ours: Meta’s day runs midnight to midnight Lagos time,
        Anthropic’s in UTC. More than 5% apart means a rate changed, so update the inputs above. For WhatsApp, open a row
        to see each category: if the message counts match but the cost doesn’t, a rate changed; if the counts differ,
        some delivery updates didn’t reach us.
      </Hint>
      <Scroll>
        <SimpleTable>
          <thead>
            <tr>
              <th>Day</th>
              <th>Provider</th>
              <th className='num'>Theirs</th>
              <th className='num'>Ours</th>
              <th className='num'>Difference</th>
            </tr>
          </thead>
          <tbody>
            {checks.map((c) => {
              const key = `${c.provider}-${c.day}`;
              const expandable = c.categories.length > 0;
              return (
                <Fragment key={key}>
                  <Row
                    className={c.flagged ? 'loss' : ''}
                    $clickable={expandable}
                    onClick={() => expandable && setOpen(open === key ? null : key)}
                    aria-expanded={expandable ? open === key : undefined}
                  >
                    <td>
                      {expandable && <Caret $open={open === key}>▸</Caret>}
                      {c.day}
                    </td>
                    <td>
                      {PROVIDERS[c.provider] ?? c.provider}
                      {c.provider === 'anthropic' && c.our_calls != null && <Sub>{c.our_calls} calls recorded</Sub>}
                    </td>
                    <td className='num'>{money(c.reported_cost_ngn)}</td>
                    <td className='num'>{money(c.our_cost_ngn)}</td>
                    <td className='num'>{pct(c.difference_pct)}</td>
                  </Row>
                  {open === key &&
                    c.categories.map((cat) => (
                      <DetailRow key={`${key}-${cat.category}`}>
                        <td />
                        <td>
                          {CATEGORY[cat.category] ?? cat.category}
                          <Sub>
                            Meta: {cat.billed_messages} billed, {cat.free_messages} free · ours: {cat.our_messages} billed
                          </Sub>
                        </td>
                        <td className='num'>{money(cat.reported_cost_ngn)}</td>
                        <td className='num'>{money(cat.our_cost_ngn)}</td>
                        <td className='num'>{pct(cat.difference_pct)}</td>
                      </DetailRow>
                    ))}
                </Fragment>
              );
            })}
            {!checks.length && (
              <tr>
                <td colSpan={5}>No checks yet. They run every morning, or check a day now.</td>
              </tr>
            )}
          </tbody>
        </SimpleTable>
      </Scroll>
    </Card>
  );
};

export default ProviderChecksCard;

const Controls = styled.form`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const Row = styled.tr<{ $clickable: boolean }>`
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

const DetailRow = styled.tr`
  td {
    background: #faf9f5;
  }
`;

const Caret = styled.span<{ $open: boolean }>`
  display: inline-block;
  width: 14px;
  margin-right: 4px;
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
`;

const Sub = styled.small`
  display: block;
  font-size: 12px;
  color: ${({ theme }) => theme.palette.grayTextColor};
`;
